import { memo, useMemo, useState } from 'react';

import { type PublicBoard } from '@air/api/types';
import { itemIsHighlightedOnShiftSelectSelector } from '@air/redux-selected-items';

import { type ItemProps } from '~/components/Gallery/types';
import { BoardCellThumbnail } from '~/components/TableView/NameCell/BoardCellThumbnail';
import { NameCellContent } from '~/components/TableView/NameCell/NameCellContent';
import { DropdownMenuWrapper } from '~/components/TableView/PrivateTableView/DropdownMenuWrapper';
import { PublicBoardNameCellTitle } from '~/components/TableView/PrivateTableView/PublicBoardNameCellTitle';
import { BOARD_NAME_CELL } from '~/constants/testIDs';
import { useAirSelector } from '~/utils/ReduxUtils';

export type PublicBoardNameCellProps = {
  isHovering: boolean;
  isSelected: boolean;
  board: PublicBoard;
} & ItemProps;

export const PublicBoardNameCell = memo(
  ({
    isHovering,
    isSelected,
    board,
    getItemMenuOptions,
    getSelectionMenuOptions,
    onItemClick,
    onItemCmdClick,
  }: PublicBoardNameCellProps) => {
    const [menuIsOpen, setMenuIsOpen] = useState(false);
    const isHighlightedFromShiftSelect = useAirSelector((st) => itemIsHighlightedOnShiftSelectSelector(st, board.id));

    const shouldShowMenu = !isHighlightedFromShiftSelect && (isHovering || menuIsOpen);

    const _title = useMemo(() => {
      return <PublicBoardNameCellTitle board={board} onClick={onItemClick} onCmdClick={onItemCmdClick} />;
    }, [board, onItemClick, onItemCmdClick]);

    const _image = useMemo(() => {
      return <BoardCellThumbnail thumbnail={board.thumbnails?.[0]} title={board.title} />;
    }, [board]);

    const dataTestId = BOARD_NAME_CELL;

    const ellipsisMenuOptions = useMemo(
      () => getItemMenuOptions({ trackLocation: 'table-item-ellipsis-menu' }),
      [getItemMenuOptions],
    );

    const selectionMenuOptions = getSelectionMenuOptions('table-multi-select-ellipsis-menu');
    const menuOptions = isSelected && selectionMenuOptions.length ? selectionMenuOptions : ellipsisMenuOptions;

    return (
      <NameCellContent data-testid={dataTestId} title={_title} image={_image}>
        {shouldShowMenu && !!menuOptions.length && (
          <DropdownMenuWrapper options={menuOptions} onOpenChange={setMenuIsOpen} dataTestId={`${dataTestId}-menu`} />
        )}
      </NameCellContent>
    );
  },
);

PublicBoardNameCell.displayName = 'PublicBoardNameCell';
